/**
 * Process Map View Component
 * Visual representation of manufacturing workflows
 * Shows the complete journey: Quote → Order → Production → Quality → Delivery
 */

import React from 'react';
import { Card, Steps, Typography, Space, Tag, Button } from 'antd';
import {
  FileTextOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './ProcessMapView.css';

const { Text, Title } = Typography;
const { Step } = Steps;

interface ProcessStep {
  title: string;
  description: string;
  status: 'wait' | 'process' | 'finish' | 'error';
  icon: React.ReactNode;
  documentNumber?: string;
  documentId?: string;
  route?: string;
  date?: string;
}

interface ProcessMapViewProps {
  processType: 'order-to-cash' | 'quote-to-production' | 'production-to-delivery';
  steps: ProcessStep[];
}

const processTypeConfig = {
  'order-to-cash': {
    title: '💰 Order to Cash Flow',
    description: 'Track the complete sales and delivery process',
    color: '#52c41a',
  },
  'quote-to-production': {
    title: '🏭 Quote to Production Flow',
    description: 'From quotation to finished goods',
    color: '#145A54',
  },
  'production-to-delivery': {
    title: '🚚 Production to Delivery Flow',
    description: 'Manufacturing through delivery',
    color: '#722ed1',
  },
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'finish':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'process':
      return <ClockCircleOutlined style={{ color: '#145A54' }} />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    default:
      return null;
  }
};

export const ProcessMapView: React.FC<ProcessMapViewProps> = ({
  processType,
  steps,
}) => {
  const navigate = useNavigate();
  const config = processTypeConfig[processType];

  const currentStepIndex = steps.findIndex(step => step.status === 'process');

  return (
    <Card 
      className="process-map-card"
      style={{ borderLeft: `4px solid ${config.color}` }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {config.title}
          </Title>
          <Text type="secondary">{config.description}</Text>
        </div>

        <Steps
          current={currentStepIndex}
          status={steps[currentStepIndex]?.status === 'error' ? 'error' : undefined}
          direction="horizontal"
          responsive={false}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={step.title}
              description={
                <Space direction="vertical" size="small" style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {step.description}
                  </Text>
                  
                  {step.documentNumber && (
                    <Tag color={step.status === 'finish' ? 'green' : 'blue'}>
                      {step.documentNumber}
                    </Tag>
                  )}

                  {step.date && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {step.date}
                    </Text>
                  )}

                  {step.route && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigate(step.route!)}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      View Details →
                    </Button>
                  )}
                </Space>
              }
              icon={step.icon}
              status={step.status}
            />
          ))}
        </Steps>

        <div style={{ 
          padding: 12, 
          background: config.color === '#52c41a' ? '#f6ffed' : '#f0f5ff', 
          borderRadius: 4,
          borderLeft: `3px solid ${config.color}`
        }}>
          <Space>
            {getStatusIcon(steps[currentStepIndex]?.status || 'wait')}
            <Text strong>
              Current Stage: {steps[currentStepIndex]?.title || 'Not Started'}
            </Text>
          </Space>
        </div>
      </Space>
    </Card>
  );
};

/**
 * Predefined Process Templates
 */
export const OrderToCashProcess: React.FC<{ salesOrderId: string }> = ({ salesOrderId }) => {
  const steps: ProcessStep[] = [
    {
      title: 'Quotation',
      description: 'Customer inquiry',
      status: 'finish',
      icon: <FileTextOutlined />,
      documentNumber: 'QUOT-2026-001',
      route: '/growth/quotations/001',
      date: '2026-02-01',
    },
    {
      title: 'Customer Order',
      description: 'Order confirmed',
      status: 'finish',
      icon: <ShoppingCartOutlined />,
      documentNumber: 'SO-2026-001',
      route: `/growth/orders/${salesOrderId}`,
      date: '2026-02-02',
    },
    {
      title: 'Production',
      description: 'Manufacturing',
      status: 'process',
      icon: <ToolOutlined />,
      documentNumber: 'WO-2026-001',
      route: '/mfg/work-orders/001',
      date: '2026-02-03',
    },
    {
      title: 'Quality Check',
      description: 'Inspection',
      status: 'wait',
      icon: <SafetyCertificateOutlined />,
    },
    {
      title: 'Delivery',
      description: 'Ship to customer',
      status: 'wait',
      icon: <CarOutlined />,
    },
    {
      title: 'Invoice',
      description: 'Payment',
      status: 'wait',
      icon: <DollarOutlined />,
    },
  ];

  return <ProcessMapView processType="order-to-cash" steps={steps} />;
};

export default ProcessMapView;
